import {Component} from 'panel';

import timing from 'common/stylesheets/mixins/timing.json';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {ToastType} from './types';
import {template} from './template';
import css from './index.styl?inline';

const TOAST_TYPE_ICONS = {
  [ToastType.ERROR]: `warning`,
  [ToastType.INFO]: `info`,
  [ToastType.SUCCESS]: `circle-checkmark`,
  [ToastType.WARNING]: `warning`,
  [ToastType.DESTRUCTIVE]: `trashcan`,
};

export class Toast extends Component<UnknownObj> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      cta: `string`,
      hide: `boolean`,
      'is-error': {type: `boolean`, deprecatedMsg: `use attr "type" instead`},
      timeout: {
        default: null,
        type: `number`,
        description: `The number of milliseconds after which to close the toast (comes with an animation)`,
      },
      type: {
        default: ToastType.INFO,
        type: `string`,
        enum: Object.values(ToastType),
        description: `The type of toast message which primarily determines the color`,
      },
      undoable: {type: `boolean`, description: `whether undo btn should be displayed`},
      'hide-cancel-button': {type: `boolean`, description: `true when hiding the x to close the toast`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        // delay removal by 2x mp-slow: 1x for the text slide in, and 1x for the button fade
        delayRemove: 2 * timing[`$mp-slow`],
      },
      helpers: {
        ctaClick: () => this._triggerAction(),
        closeClick: () => this._triggerClose(),
        undoClick: () => this._triggerUndo(),
        getIcon: () => TOAST_TYPE_ICONS[this.attr(`type`)],
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    [`action`, `undo`].forEach((eventName) => {
      this.addEventListener(
        eventName,
        (ev) => {
          // Wrapping this in a set timeout as a hack around the order of event listener invocations.
          // Without it, this event listener is called before any other attached listeners for 'action'
          // or 'undo' so the ability to prevent the default close is not possible.
          setTimeout(() => {
            if (!ev.defaultPrevented) {
              this._triggerClose();
            }
          }, 0);
        },
        true,
      );
    });

    const timeout = this.attr(`timeout`);
    if (timeout) {
      setTimeout(() => {
        this._triggerClose();
      }, timeout);
    }
  }

  private _triggerAction() {
    this.dispatchEvent(new CustomEvent(`action`, {cancelable: true}));
  }

  private _triggerClose() {
    this.dispatchEvent(new CustomEvent(`close`));
  }

  private _triggerUndo() {
    this.dispatchEvent(new CustomEvent(`undo`, {cancelable: true}));
  }
}

export const ToastEl = defineAndMakeJsxFactory(`mp-toast`, Toast);
